import type { ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * Semaforo budget a quattro livelli (brief §15).
 *
 * Il rosso e' riservato ai problemi reali: un possibile sforamento e' arancione,
 * non rosso. Microcopy in tono umano (brief §40): "Ci stai dentro", non
 * "Budget utilization: 73%".
 *
 * Accessibilita': livello veicolato da glifo + testo, non dal solo colore.
 */

export type BudgetLevelValue = 'ok' | 'warn' | 'over' | 'blocked';

interface LevelPresentation {
  readonly glyph: string;
  readonly defaultLabel: string;
  readonly classes: string;
  readonly srPrefix: string;
}

const PRESENTATION: Record<BudgetLevelValue, LevelPresentation> = {
  ok: {
    glyph: '●',
    defaultLabel: 'Ci stai dentro',
    classes: 'bg-[var(--budget-ok-soft)] text-[var(--budget-ok)]',
    srPrefix: 'Budget: nel previsto.',
  },
  warn: {
    glyph: '●',
    defaultLabel: 'Attenzione al budget',
    classes: 'bg-[var(--budget-warn-soft)] text-[var(--budget-warn)]',
    srPrefix: 'Budget: vicino al limite.',
  },
  over: {
    glyph: '▲',
    defaultLabel: 'Potresti sforare',
    classes: 'bg-[var(--budget-over-soft)] text-[var(--budget-over)]',
    srPrefix: 'Budget: potresti superarlo.',
  },
  blocked: {
    glyph: '■',
    defaultLabel: 'Fuori budget',
    classes: 'bg-[var(--budget-blocked-soft)] text-[var(--budget-blocked)]',
    srPrefix: 'Budget: superato.',
  },
};

export interface BudgetPillProps {
  readonly level: BudgetLevelValue;
  readonly label?: string;
  readonly className?: string;
}

export function BudgetPill({ level, label, className }: BudgetPillProps): ReactNode {
  const presentation = PRESENTATION[level];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--r-full)] px-2.5 py-1',
        'text-[13px] font-medium leading-5',
        presentation.classes,
        className,
      )}
    >
      <span aria-hidden="true">{presentation.glyph}</span>
      <span className="sr-only">{presentation.srPrefix}</span>
      <span>{label ?? presentation.defaultLabel}</span>
    </span>
  );
}
